import type { BoQCell, BoQRow, FieldCells, FieldKey, RowClassification } from './types';
import { classifyRow } from './row-classifier';
import { toDisplayText } from '../utils/text';

export interface CellInput {
  readonly value?: unknown;
  readonly text?: string;
  readonly formula?: string | null;
  readonly formulaResultAvailable?: boolean;
}

export type CellInputValue =
  CellInput | string | number | bigint | boolean | Date | null | undefined;

export function createCell(input: CellInputValue): BoQCell {
  if (isCellInput(input)) {
    const value = input.value ?? null;
    const formula = input.formula ?? null;
    return {
      value,
      text: input.text ?? toDisplayText(value),
      formula,
      formulaResultAvailable: input.formulaResultAvailable ?? formula === null,
    };
  }

  return {
    value: input,
    text: toDisplayText(input),
    formula: null,
    formulaResultAvailable: true,
  };
}

export function createFieldCells(
  values: Readonly<Partial<Record<FieldKey, CellInputValue>>>,
): FieldCells {
  const entries = Object.entries(values).map(
    ([field, value]) => [field, createCell(value)] as const,
  );
  return Object.fromEntries(entries);
}

export interface CreateRowInput {
  readonly sheetName: string;
  readonly excelRow: number;
  readonly values: Readonly<Partial<Record<FieldKey, CellInputValue>>>;
  readonly classification?: RowClassification;
}

export function createBoQRow(input: CreateRowInput): BoQRow {
  const cells = createFieldCells(input.values);
  return {
    sheetName: input.sheetName,
    excelRow: input.excelRow,
    cells,
    classification: input.classification ?? classifyRow(cells),
  };
}

export function getCell(row: BoQRow, field: FieldKey): BoQCell | undefined {
  return row.cells[field];
}

export function getCellValue(row: BoQRow, field: FieldKey): unknown {
  return getCell(row, field)?.value ?? null;
}

export function getCellText(row: BoQRow, field: FieldKey): string {
  const cell = getCell(row, field);
  return cell?.text ?? toDisplayText(cell?.value);
}

function isCellInput(value: CellInputValue): value is CellInput {
  if (typeof value !== 'object' || value === null || value instanceof Date) return false;
  const record = value as Record<string, unknown>;
  return (
    Object.hasOwn(record, 'value') ||
    Object.hasOwn(record, 'text') ||
    Object.hasOwn(record, 'formula') ||
    Object.hasOwn(record, 'formulaResultAvailable')
  );
}
